import type { UseInViewWithDwellOptions } from '@notino/react-web-utils';
import { IStock } from '@notino/shared/definitions/custom-definitions';
import {
  GetProductDetailAdsQuery,
  Maybe,
} from '@notino/shared/definitions/types';
import {
  createSponsoredAdsReporter,
  UnrenderedReason,
  type AdProvider,
  type RenderedProduct,
  type UnrenderedProduct,
} from '@notino/web-tracking';

import { NewRelicBrowser } from '@lib/newRelic/NewRelicBrowser';

export const RETAIL_MEDIA_REPORT_URL = '/api/retail-media/report';

type ProductCarousel = NonNullable<
  NonNullable<GetProductDetailAdsQuery['ProductDetailAds']>['productCarousel']
>;

type CarouselItem = NonNullable<NonNullable<ProductCarousel['items']>[number]>;
type CarouselProduct = NonNullable<
  NonNullable<CarouselItem['products']>[number]
>;

export type SponsoredAdsVpProduct = NonNullable<CarouselProduct['vpProduct']>;

/** One delivered product occurrence, in delivery order, with its beacon payload URLs. */
export interface SponsoredAdsOccurrence {
  productId: string;
  vpProduct: Maybe<SponsoredAdsVpProduct>;
  onViewPayload?: Maybe<string>;
  onClickPayload?: Maybe<string>;
  onBasketChangePayload?: Maybe<string>;
  onWishlistPayload?: Maybe<string>;
}

/** A delivered occurrence whose product can be displayed. */
type DisplayableOccurrence = SponsoredAdsOccurrence & {
  vpProduct: SponsoredAdsVpProduct;
};

/** A delivered occurrence that renders, together with the slot it occupies. */
export type RenderedOccurrence = DisplayableOccurrence & { slotId: number };

/**
 * Why a delivered product will not appear in the carousel, or `null` when it renders.
 * Either the gateway could not resolve it at all (`vpProduct` is null), or it resolved to
 * a product that is out of stock — neither is displayed, and the provider is told which
 * of the two happened.
 */
export const getUnrenderedReason = (
  occurrence: SponsoredAdsOccurrence
): UnrenderedReason | null => {
  if (!occurrence.vpProduct) {
    return UnrenderedReason.GenericError;
  }

  if (occurrence.vpProduct.stockAvailability?.code === IStock.outOfStock) {
    return UnrenderedReason.ItemNotInStock;
  }

  return null;
};

/**
 * Provider is echoed back untouched in every tracking event. Default to `Criteo`
 * since the product-detail placement is Criteo-first; unknown strings pass through.
 */
export const normalizeProvider = (provider?: Maybe<string>): AdProvider =>
  (provider ?? 'Criteo') as AdProvider;

/**
 * Provider-specific viewability rule for the shared `useInViewWithDwell` hook.
 * Kept out of the hook so it stays provider-agnostic. Criteo counts an impression
 * only after the element stays ≥50% visible for a 1000ms dwell.
 */
export const getSponsoredPlacementInViewOptions = (
  provider: AdProvider
): UseInViewWithDwellOptions => {
  if (provider === 'Criteo') {
    return { threshold: 0.5, dwellMs: 1000 };
  }

  // Martailer / default keeps the historical central-viewport rule, no dwell.
  return { rootMargin: '-20% 0px -20% 0px' };
};

/**
 * Provider-specific click rule. Criteo requires `ProductOnClick` to fire for
 * right-click → "open in new tab" as well; `contextmenu` is the only signal the
 * browser gives us for that, so a dismissed right-click reports too. Other providers
 * keep the default left/middle-click-only reporting.
 */
export const shouldReportClickOnContextMenu = (provider: AdProvider): boolean =>
  provider === 'Criteo';

/**
 * Provider-specific report-frequency rule. Criteo treats the product interaction
 * payloads as single-use: one click / basket change / wishlist add per product per
 * delivery, no matter how many times the user repeats it. Other providers keep
 * reporting every interaction.
 */
export const shouldReportInteractionsOnce = (provider: AdProvider): boolean =>
  provider === 'Criteo';

/**
 * Flatten `productCarousel.items[].products[]` into an ordered list of occurrences.
 * Deduplication is intentionally NOT applied — Criteo does not support it, so the
 * same product may legitimately appear more than once.
 */
export const flattenCarouselProducts = (
  carousel?: Maybe<ProductCarousel>
): SponsoredAdsOccurrence[] => {
  const occurrences: SponsoredAdsOccurrence[] = [];

  for (const item of carousel?.items ?? []) {
    for (const product of item?.products ?? []) {
      if (!product?.productId) {
        continue;
      }
      occurrences.push({
        productId: String(product.productId),
        vpProduct: product.vpProduct,
        onViewPayload: product.payloads?.onViewPayload,
        onClickPayload: product.payloads?.onClickPayload,
        onBasketChangePayload: product.payloads?.onBasketChangePayload,
        onWishlistPayload: product.payloads?.onWishlistPayload,
      });
    }
  }

  return occurrences;
};

/**
 * The occurrences that actually render, in carousel order, each numbered with the slot it
 * occupies. This is the single source of truth for what the carousel shows and for what is
 * reported as rendered. Slots exist only for reporting (`renderedProducts` and the product
 * onClick), so they are derived here rather than carried around on the delivery data — and
 * they count displayed products only: an unresolvable or out-of-stock product is skipped
 * in the carousel, so everything after it moves up (a delivery of 5 whose 3rd product is
 * dropped renders as slots 0-3, not 0,1,3,4).
 */
export const getRenderedOccurrences = (
  occurrences: SponsoredAdsOccurrence[]
): RenderedOccurrence[] =>
  occurrences
    .filter(
      (occurrence): occurrence is DisplayableOccurrence =>
        getUnrenderedReason(occurrence) === null
    )
    .map((occurrence, slotId) => ({ ...occurrence, slotId }));

/** Placement-level payloads (onLoad/onView) from the first carousel item. */
export const getPlacementPayloads = (carousel?: Maybe<ProductCarousel>) =>
  carousel?.items?.find((item) => item?.payloads)?.payloads ?? null;

/**
 * Split the delivered occurrences into what actually rendered vs. what did not, for the
 * `PlacementOnLoad` event. A product is "rendered" when the gateway resolved it to a
 * catalog product that is in stock; the rest are reported as unrendered with the reason
 * that applies to them, so the provider can adjust future delivery.
 */
export const partitionOccurrencesByRender = (
  occurrences: SponsoredAdsOccurrence[]
): {
  renderedProducts: RenderedProduct[];
  unrenderedProducts: UnrenderedProduct[];
} => {
  const unrenderedProducts: UnrenderedProduct[] = [];

  for (const occurrence of occurrences) {
    const reasonCode = getUnrenderedReason(occurrence);
    if (reasonCode) {
      unrenderedProducts.push({
        productId: Number(occurrence.productId),
        reasonCode,
      });
    }
  }

  return {
    renderedProducts: getRenderedOccurrences(occurrences).map(
      ({ productId, slotId }) => ({ productId: Number(productId), slotId })
    ),
    unrenderedProducts,
  };
};

/**
 * Every delivered product reported as unrendered because the delivery did not contain
 * enough displayable products to fill the placement. Each product keeps the reason that
 * actually applies to it — unresolvable or out of stock — while a product that could have
 * been displayed went unrendered only because the placement was dropped, which is Criteo
 * reason 102, "minimum SKUs requirement".
 */
export const asBelowMinimumUnrendered = (
  occurrences: SponsoredAdsOccurrence[]
): UnrenderedProduct[] =>
  occurrences.map((occurrence) => ({
    productId: Number(occurrence.productId),
    reasonCode:
      getUnrenderedReason(occurrence) ??
      UnrenderedReason.MinimumSKUsRequirement,
  }));

/**
 * Sends sponsored-ads tracking events to the Retail Media report endpoint through the
 * shared web-tracking reporter (`fetch` keepalive, falling back to Beacon). It also
 * deduplicates Criteo events across documents for the session; the in-document guards in
 * `useSponsoredAdsTracking` stay. No-op on the server; tracking never throws, failures go
 * to New Relic.
 */
export const sponsoredAdsReporter = createSponsoredAdsReporter({
  endpoint: RETAIL_MEDIA_REPORT_URL,
  onError: (error) =>
    NewRelicBrowser.agent.noticeError(
      '[RMA tracking] report failed: ' + (error as Error).message
    ),
});
